import {
    OscarBooking,
    OscarFormStateRequest,
    OscarRegister,
    OscarRegisterBody,
    OscarRegisterRequest,
    OscarSite,
    OscarSiteBody, OscarSubmitBody
} from "@/lib/oscar/ApiModels.ts";

export default class Api {

    /**
     * Build the full URL for the API call
     *
     * @param path The path to the API endpoint
     * @returns The full URL
     */
    private static buildUrl(path: string): string {
        return `${import.meta.env.VITE_BASE_API_URL}/${path}`;
    }

    /**
     * Register the session with the Oscar API
     */
    static async register(brand: string): Promise<OscarRegister> {
        const requestData = {
            brand: brand,
            userAgent: null,
            engine: "Oscar-Web/1100",
            fresh: false,
        } as OscarRegisterRequest;

        const token = sessionStorage.getItem("oscar_token");
        const headers: HeadersInit = {
            "Content-type": "application/json; charset=UTF-8"
        };

        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        const response = await fetch(this.buildUrl('register'), {
            method: "POST",
            body: JSON.stringify(requestData),
            headers: headers
        });

        const registerBody = (await response.json()) as OscarRegisterBody | null;
        if (!registerBody || registerBody.error || !registerBody.body) {
            throw new Error("Failed to register session");
        }

        // Cache token.
        sessionStorage.setItem("oscar_token", registerBody.body.token);

        return registerBody.body;
    }

    /**
     * Check if the location is within the service area
     *
     * @param token The session token
     * @param lat The latitude
     * @param lng The longitude
     */
    static async checkLocation(token: string, lat: number, lng: number): Promise<OscarSite> {
        const response = await fetch(this.buildUrl('site'), {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                "Content-type": "application/json; charset=UTF-8"
            },
            body: JSON.stringify({ lat: lat, lng: lng }),
        });
        const result = await response.json() as OscarSiteBody | null;
        if (!result || result.error || !result.body) {
            throw new Error('Failed to check service area');
        }

        return result.body;
    }

    /**
     * Send the state of the form to the Oscar API
     *
     * @param token The session token
     * @param state The state to send
     */
    static async sendState(token: string, state: OscarFormStateRequest): Promise<void> {
        try {
            const response = await fetch(this.buildUrl('state'), {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    "Content-type": "application/json; charset=UTF-8"
                },
                body: JSON.stringify(state),
            });

            if (response.status !== 202) {
                throw new Error('Failed to update state');
            }
        } catch (e) {
            console.error(e);
            console.warn("Failed to update state");
        }
    }

    /**
     * Submit the booking
     *
     * @param token The session token
     * @param state The state to send
     */
    static async submitBooking(token: string, state: OscarFormStateRequest): Promise<OscarBooking> {
        const response = await fetch(this.buildUrl('submit'), {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`,
                "Content-type": "application/json; charset=UTF-8"
            },
            body: JSON.stringify(state),
        });

        const registerBody = (await response.json()) as OscarSubmitBody | null;
        if (!registerBody) {
            throw new Error("Failed to place booking, please try again.");
        } else if (registerBody.error) {
            throw new Error(registerBody.error);
        } else if (!registerBody.body) {
            throw new Error("Failed to place booking, please try again.");
        }

        return registerBody.body;
    }
}